import { FeatureFlagKey } from '@/modules/common/hooks/use-feature-flags';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { SettingType } from '@/modules/settings/types/setting-type';

/**
 * Represents a permission requirement that can be evaluated against user's permissions
 * Supports complex requirements using AND/OR logic, feature flags, and setting type access
 */
export class PermissionRequirement {
  denyAlways: boolean = false;
  policies: OpsiumPolicy[] = [];
  featureFlags: FeatureFlagKey[] = [];
  settings: SettingType[] = [];
  OR: PermissionRequirement[] = [];
  AND: PermissionRequirement[] = [];

  constructor(init?: Partial<PermissionRequirement>) {
    if (init) {
      this.denyAlways = init.denyAlways ?? false;
      this.policies = init.policies ?? [];
      this.featureFlags = init.featureFlags ?? [];
      this.settings = init.settings ?? [];
      this.OR = init.OR ?? [];
      this.AND = init.AND ?? [];
    }
  }
}

/**
 * Evaluates if a user meets the permission requirement
 * @param requirement - The permission requirement to check
 * @param hasPermission - Function that checks if user has a specific permission
 * @param isFeatureEnabled - Function that checks if a feature flag is enabled
 * @param hasSettingAccess - Function that checks if user has access to a specific setting type
 * @returns true if the user meets the requirement, false otherwise
 */
export function evaluatePermissionRequirement(
  requirement: PermissionRequirement,
  hasPermission: (policy: OpsiumPolicy) => boolean,
  isFeatureEnabled?: (flag: FeatureFlagKey) => boolean,
  hasSettingAccess?: (settingType: SettingType) => boolean
): boolean {
  if (requirement.denyAlways) {
    return false;
  }

  // Check feature flags first - all must be enabled
  if (requirement.featureFlags.length > 0 && isFeatureEnabled) {
    const allFlagsEnabled = requirement.featureFlags.every(flag => isFeatureEnabled(flag));
    if (!allFlagsEnabled) {
      return false;
    }
  }

  // Check settings access - all required settings must be available
  if (requirement.settings.length > 0 && hasSettingAccess) {
    const allSettingsAvailable = requirement.settings.every(setting => hasSettingAccess(setting));
    if (!allSettingsAvailable) {
      return false;
    }
  }

  // Check direct policies - user must have at least one (unless empty)
  const hasPolicies =
    requirement.policies.length === 0 || requirement.policies.some(policy => hasPermission(policy));

  if (!hasPolicies) {
    return false;
  }

  // Check AND requirements - all must be satisfied
  const hasAndRequirements =
    requirement.AND.length === 0 ||
    requirement.AND.every(subRequirement =>
      evaluatePermissionRequirement(
        subRequirement,
        hasPermission,
        isFeatureEnabled,
        hasSettingAccess
      )
    );

  if (!hasAndRequirements) {
    return false;
  }

  // Check OR requirements - at least one must be satisfied
  return (
    requirement.OR.length === 0 ||
    requirement.OR.some(subRequirement =>
      evaluatePermissionRequirement(
        subRequirement,
        hasPermission,
        isFeatureEnabled,
        hasSettingAccess
      )
    )
  );
}
